import { HotChipKind, type BroadcastEntry, type BroadcastList } from '../gen/catalog_pb.js';
import type { CalendarSnapshot } from '../catalog.js';
import type { TopGame, TopGamesSelection } from '../ui/top-games.js';
import { teamFlag } from '../federation-flag.js';
import { personName } from '../display-name.js';
import type { LiveGame } from './types.js';

/** The round page's Top games panel shows at most this many producer picks. */
export const TOP_GAMES_LIMIT = 5;

/**
 * How long a pick of a round in `recently_finished` is still admitted, measured
 * from the pick's own `computed_at_ms`.
 *
 * mirror-of: relay-server-rs/relay-ingest/src/heat/tracker.rs `GRACE_S` — the
 * producer's own window for a finished breaking board to remain reviewable.
 * The two agree so that what the producer is still willing to say about a
 * finished board, the site is still willing to show.
 */
export const HOT_BOARDS_FINISHED_GRACE_MS = 600_000;

/**
 * How far AHEAD of this browser's clock a pick's `computed_at_ms` may sit and
 * still be read as "just computed".
 *
 * `computed_at_ms` is relay-ingest's clock and `nowMs` is the visitor's, so a
 * negative age is ordinary: phones and laptops run seconds out, and an NTP
 * step lands in the same place. Without a floor, though, a negative age
 * satisfies the grace for ever, and one bogus future stamp from a producer
 * would pin a finished board to the panel for as long as the round stays in
 * `recently_finished`. A minute is wider than any skew the site has seen and
 * far inside the ten-minute grace, so the two rules cannot swap places.
 */
export const HOT_BOARDS_CLOCK_SKEW_MS = 60_000;

/**
 * `BroadcastList.hot_boards` shaped for the Top games panel, in WIRE order (the
 * Heating Up producer ranks tension-descending; the client never re-sorts or
 * infers picks), minus any entry whose round the same response's calendar does
 * not admit, and minus a blank (unnavigable) round id.
 *
 * A round is admitted when it is `active`, and — the web's widening of the
 * canonical guard, 2026-09-22 — when it is in `recently_finished` and the pick
 * was computed within {@link HOT_BOARDS_FINISHED_GRACE_MS} of `nowMs`. The
 * calendar and the feed come from different producers on different cadences:
 * relay-control files a round under `recently_finished` the poll after its
 * stream ends (`calendar.rs` RES-1), while `hot_boards` is the heat producer's
 * LAST push — up to a 30 s tick later in the ordinary case, and up to the
 * feed's 180 s TTL when relay-ingest restarts at round end. The Olympiad's
 * sections close on their last game, which is the game the producer pushes as
 * a breaking finish, so the active-only guard dropped exactly the pick worth
 * showing. A pick with no `computed_at_ms` under a finished round cannot have
 * its age bounded and is dropped, as is one stamped further than
 * {@link HOT_BOARDS_CLOCK_SKEW_MS} ahead of this clock; an ACTIVE round's
 * picks are never aged.
 *
 * mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HotBoardPresentation.kt
 * (`HotBoardPresentation.hotBoards`, consistency guard §3.7 case 16) for the
 * `active` arm; the app does not yet apply the finished grace. Agreement test:
 * src/live/hot-boards.test.ts. Only the fields the panel renders are kept, plus
 * `tourId`, which the round page scopes by ({@link heatScope}), the position
 * `fen` a stream-free mini-board draws, and `breaking` (a MATE chip, the
 * poster's `is_breaking`) for the home band's admission ({@link headedForResult}).
 */
export function hotBoards(list: Pick<BroadcastList, 'active' | 'recentlyFinished' | 'hotBoards'>, nowMs: number): TopGame[] { // mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HotBoardPresentation.kt
  if (list.hotBoards.length === 0) return [];
  const liveRoundIds = new Set(list.active.map(entry => entry.roundId));
  const finishedRoundIds = new Set(list.recentlyFinished.map(entry => entry.roundId));
  const admitted = (entry: BroadcastList['hotBoards'][number]): boolean => {
    if (entry.roundId === '') return false;
    if (liveRoundIds.has(entry.roundId)) return true;
    if (!finishedRoundIds.has(entry.roundId)) return false;
    const computedAtMs = Number(entry.computedAtMs);
    if (computedAtMs <= 0) return false;
    // Bounded on BOTH sides: a stamp ahead of this clock is skew (or a bogus
    // producer value) rather than freshness, so it is admitted only within
    // {@link HOT_BOARDS_CLOCK_SKEW_MS} and never indefinitely.
    const ageMs = nowMs - computedAtMs;
    return ageMs >= -HOT_BOARDS_CLOCK_SKEW_MS && ageMs <= HOT_BOARDS_FINISHED_GRACE_MS;
  };
  return list.hotBoards
    .filter(admitted)
    .map(({ roundId, tourId, boardId, whiteName, blackName, eventName, roundName, headline, tension, fen, chips }) =>
      ({ roundId, tourId, boardId, whiteName, blackName, eventName, roundName, headline, tension, fen,
        breaking: chips.some(chip => chip.kind === HotChipKind.MATE) }));
}

/**
 * Which picks belong on a round page's Heating up band: this tournament's.
 *
 * `BroadcastList.hot_boards` is ONE global feed (every live event's hottest
 * boards), and the band used to draw it unscoped — the Olympiad Round 7 page
 * showed a Zagreb Open card (owner, 2026-09-22). The page's tournament is its
 * sibling rounds (`round-rail.ts` `siblingRounds`: the same round of the same
 * event, so every section of a merged Olympiad round). A pick is in scope when
 * its round is one of those rounds, or its tour is one of their tours (another
 * round of the same section, e.g. one still inside the finished grace). A blank
 * tour id never matches. Before the calendar answers there are no siblings and
 * the scope is the page's own round alone, so no off-event card can flash.
 *
 * Web-only: the app's Heating Up surface is the Live hub hero, a global list,
 * so `HotBoardPresentation.kt` has no tournament scope to mirror.
 */
export interface HeatScope { roundIds: ReadonlySet<string>; tourIds: ReadonlySet<string> }

export function heatScope(roundId: string, siblings: readonly { entry: Pick<BroadcastEntry, 'roundId' | 'tourId'> }[]): HeatScope {
  const roundIds = new Set([roundId, ...siblings.map(row => row.entry.roundId)]);
  const tourIds = new Set(siblings.map(row => row.entry.tourId).filter(tourId => tourId !== ''));
  return { roundIds, tourIds };
}

export const inScope = (game: TopGame, scope: HeatScope): boolean =>
  scope.roundIds.has(game.roundId) || (game.tourId !== undefined && game.tourId !== '' && scope.tourIds.has(game.tourId));

/** One Heating Up card, as drawn in the round page's bottom band. */
export interface HeatCard {
  key: string;
  roundId: string;
  boardId: number;
  /** The event ("46th FIDE Chess Olympiad … | Women | III"), blank when the producer left it blank. */
  event: string;
  /** The round ("Round 2"), likewise. */
  round: string;
  white: { name: string; flag: string | null };
  black: { name: string; flag: string | null };
  /** The producer's one-line hook, verbatim. */
  headline: string;
  /** Heat 0-100, or null when the entry carries none. */
  tension: number | null;
  /** The board on the page's board right now. */
  current: boolean;
}

/**
 * The bottom band's cards: producer order, only the picks in the page's
 * {@link heatScope}, THEN capped at {@link TOP_GAMES_LIMIT} — so the count
 * ("3 picks") is of this tournament's picks, and other events' picks earlier in
 * the wire order cannot crowd this one's out.
 * `HotBoardEntry` has no federation, so a flag appears only when the entry was
 * given one or the pick is a board of the round already loaded, whose
 * `PlayerCard.federation` is on the page. Empty in, empty out: the band hides.
 */
export function heatCards(selection: TopGamesSelection | undefined, page: {
  roundId: string;
  boardId: number | null;
  games: readonly LiveGame[];
  /** This tournament ({@link heatScope}); picks outside it are not drawn. */
  scope: HeatScope;
  /**
   * Where a pick's (round, board) sits on this page, or null when it is not
   * here. Default: only the page's own round, board ids as-is. A merged round
   * (`live/merged-round.ts` `pageBoardId`) also answers for its sibling sections.
   */
  pageBoard?: (roundId: string, boardId: number) => number | null;
}): HeatCard[] {
  const locate = page.pageBoard ?? ((roundId: string, boardId: number) => (roundId === page.roundId ? boardId : null));
  return (selection?.entries ?? []).filter(game => inScope(game, page.scope)).slice(0, TOP_GAMES_LIMIT).map(game => {
    const here = locate(game.roundId, game.boardId);
    const loaded = here === null ? undefined : page.games.find(board => board.boardId === here);
    const tension = game.tension;
    return {
      key: `${game.roundId}-${game.boardId}`, roundId: game.roundId, boardId: game.boardId,
      event: game.eventName?.trim() ?? '', round: game.roundName?.trim() ?? '',
      white: { name: personName(game.whiteName), flag: teamFlag(game.whiteFederation ?? loaded?.white.federation) },
      black: { name: personName(game.blackName), flag: teamFlag(game.blackFederation ?? loaded?.black.federation) },
      headline: game.headline ?? '',
      tension: tension === undefined ? null : Math.max(0, Math.min(100, tension)),
      current: here !== null && here === page.boardId,
    };
  });
}

/**
 * A published selection: producer order, uncapped — the feed is global, and the
 * cap applies only after the page has scoped it ({@link heatCards}). Empty stays empty.
 */
export function topGamesSelection(games: readonly TopGame[]): TopGamesSelection {
  return { entries: [...games], preview: false };
}

export interface WatchTopGamesDeps {
  /** The memoised `ListBroadcasts` snapshot (`loadCalendarSnapshot`). */
  load: () => Promise<CalendarSnapshot>;
  doc?: Pick<Document, 'hidden'> & {
    addEventListener(type: 'visibilitychange', fn: () => void): void;
    removeEventListener(type: 'visibilitychange', fn: () => void): void;
  };
  /** `CALENDAR_TTL_MS`: never faster than the calendar memo (the producer re-pushes every ~30 s). */
  intervalMs: number;
  now?: () => number;
}

/**
 * Keep an open page's picks current: one read now, then one per interval while
 * the tab is visible. A hidden tab schedules nothing; showing it again reads
 * at once if the interval has passed. A failed read keeps the last picks.
 * Returns the stop function; nothing is published after it runs.
 */
export function watchTopGames(update: (selection: TopGamesSelection) => void, deps: WatchTopGamesDeps): () => void {
  const doc = deps.doc ?? document;
  const intervalMs = deps.intervalMs;
  const now = deps.now ?? Date.now;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastAt = -Infinity;

  const schedule = (delay: number): void => {
    clearTimeout(timer);
    timer = setTimeout(tick, Math.max(0, delay));
  };
  function tick(): void {
    timer = undefined;
    if (stopped || doc.hidden) return;
    lastAt = now();
    deps.load().then(
      snapshot => { if (!stopped) update(topGamesSelection(snapshot.topGames)); },
      () => undefined,
    );
    schedule(intervalMs);
  }
  const onVisibility = (): void => {
    if (stopped) return;
    if (doc.hidden) { clearTimeout(timer); timer = undefined; return; }
    if (timer === undefined) schedule(lastAt + intervalMs - now());
  };
  doc.addEventListener('visibilitychange', onVisibility);
  tick();
  return () => {
    stopped = true;
    clearTimeout(timer);
    doc.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * The tournament page's "Live now" band draws at most this many mini-boards:
 * the producer's own top-K (`rank_feed`, relay-ingest `tracker.rs` top-K = 4).
 */
export const LIVE_NOW_LIMIT = 4;

/**
 * The tournament's live picks, in producer order: only this tournament's
 * ({@link inScope}), capped at {@link LIVE_NOW_LIMIT}. Empty in, empty out.
 */
export function liveNowPicks(selection: TopGamesSelection | undefined, scope: HeatScope): TopGame[] {
  return (selection?.entries ?? []).filter(game => inScope(game, scope)).slice(0, LIVE_NOW_LIMIT);
}

/** One band's identity for a pick: its (round, board). */
export const pickKey = (game: Pick<TopGame, 'roundId' | 'boardId'>): string => `${game.roundId}-${game.boardId}`;

/**
 * The order a band shows its picks in, so that a game never changes place while
 * it stays in the band (hot-games layout, 2026-09-22: "a finishing game moves
 * from one list to the other and never reshuffles within one").
 *
 * The producer re-ranks by CURRENT heat every tick, so its order alone would
 * swap two boards under the reader's cursor every 30 s. Instead: a pick still
 * present keeps the slot it had; a pick that left frees its slot; new picks, in
 * producer rank order, fill the freed slots top-down and then append. The first
 * paint is therefore exactly the producer order, and a band whose membership
 * and ranks did not change repaints identically.
 */
export function stickySlots(previous: readonly string[], ranked: readonly string[], limit: number = LIVE_NOW_LIMIT): string[] {
  const wanted = new Set(ranked.slice(0, limit));
  const slots: (string | null)[] = previous.map(key => (wanted.has(key) ? key : null));
  const placed = new Set(slots.filter((key): key is string => key !== null));
  for (const key of ranked.slice(0, limit)) {
    if (placed.has(key)) continue;
    const free = slots.indexOf(null);
    if (free === -1) slots.push(key);
    else slots[free] = key;
    placed.add(key);
  }
  return slots.filter((key): key is string => key !== null).slice(0, limit);
}

/**
 * The home page's "headed for a result" floor: a live pick at or above this
 * tension is admitted even without a mate on the board (owner ruling O3,
 * 2026-09-22: "breaking mate or heat >= 70", the social poster's admission
 * floor without its spacing and budget).
 */
export const HOME_HOT_MIN_TENSION = 70; // mirror-of: tools/social-poster/social_poster/gates.py DEFAULT_MIN_TENSION

/** The home band shows one hero and up to three more. */
export const HOME_HOT_LIMIT = 4;

/**
 * Is a live pick "headed for a result" (owner ruling O3): a breaking board (a
 * MATE chip, the poster's `is_breaking`) or tension at {@link HOME_HOT_MIN_TENSION}+.
 */
export function isHeadedForResult(game: TopGame): boolean {
  return game.breaking === true || (game.tension ?? 0) >= HOME_HOT_MIN_TENSION;
}

/**
 * Which global live picks the home band shows (owner, 2026-09-23: the home page
 * has a game-level Heating-up panel whenever anything is live). Every live pick
 * the producer made — already gated by the tracker's enter threshold — with the
 * ones {@link isHeadedForResult} first, each group in producer order, capped at
 * {@link HOME_HOT_LIMIT}. Picks the calendar admitted only for the finished grace
 * are left out: the band is about LIVE games.
 */
export function headedForResult(games: readonly TopGame[], liveRoundIds: ReadonlySet<string>): TopGame[] {
  const live = games.filter(game => liveRoundIds.has(game.roundId));
  return [...live.filter(isHeadedForResult), ...live.filter(game => !isHeadedForResult(game))].slice(0, HOME_HOT_LIMIT);
}

/**
 * Where a link to one live board goes. ONE helper so the tournament page's
 * board scheme (layout lane L1: `/edition/<id>?round=&board=`) drops in here
 * and every hot-games surface follows; until then it is today's board URL,
 * which every surface already opens. `editionId` is accepted now so no caller
 * changes when the scheme does.
 */
export function editionBoardHref(_editionId: string | null | undefined, roundId: string, board: number | string): string {
  return `/round/${encodeURIComponent(roundId)}?board=${encodeURIComponent(String(board))}`;
}
